/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, FC } from 'react';
import clsx from 'clsx';
import {
  Button,
  Image,
  Icon,
  Types,
  Text,
  tags,
  RichText,
  Heading,
  LogoContainer,
  LogoContainerBackgroundColor,
} from '@marriott/mi-ui-library';
import { useGetBreakpoint, getClassForBreakpoint } from '../../modules/utils/helpers';
import { StyledPropertyDetails } from './PropertyDetails.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { vertical } from '../../modules/utils/constants/constants';
import { PropertyDetailsProps } from './PropertyDetails.types';

const { size } = Types;
const { NEXT_PUBLIC_AEM_SITE } = process.env;

export const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  header,
  headerFontSize,
  subHeader,
  subHeaderFontSize,
  descriptionText,
  descriptionFontSize,
  city,
  state,
  country,
  primaryCtaLabel,
  primaryCtaLink,
  primaryCtaType,
  secondaryCtaLabel,
  secondaryCtaLink,
  secondaryCtaType,
  openInNewTabSecondary,
  openInNewTabPrimary,
  dynamicMedia,
  brandLogoPath,
  cardVariation,
  styleclass = '',
  logoPath,
  trackingProperties,
  headerTag,
  subHeaderTag,
}) => {
  const [breakpoint, setBreakpoint] = useState<string | undefined>(useGetBreakpoint());
  const isCardVertical = cardVariation?.toLowerCase() === vertical.toLowerCase() ? true : false;

  // Reset breakpoint on window resize.
  useEffect(() => {
    const handleResize = () => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setBreakpoint(useGetBreakpoint());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getOutletShortAddress = (city?: string, state?: string, country?: string) => {
    let address = '';

    if (city) {
      address += city;
    }

    if (state) {
      if (!(address.endsWith(',') || address.endsWith(' '))) address += ', ';
      address += state;
    }

    if (country) {
      if (!(address.endsWith(',') || address.endsWith(' '))) address += ', ';
      address += country || '';
    }
    return address;
  };

  const buttonCallback = (link: string, openInNewTab: boolean) => {
    window.open(link, openInNewTab ? '_blank' : '_self');
  };

  const getCtaClasses = (ctaType: string, openInNewTab: boolean, customClass: string = '') => {
    switch (ctaType) {
      case 'primaryButton':
        customClass += ' m-button-primary m-button-s';
        break;
      case 'secondaryButton':
        customClass += ' m-button-secondary m-button-s';
        break;
      case 'standaloneLink':
        customClass += ' m-link-action';
        break;
      case 'tertiaryLink':
        customClass += ' m-link-tertiary-button';
        break;
      default:
        break;
    }

    if (openInNewTab) {
      if (ctaType === 'primaryButton' || ctaType === 'secondaryButton') {
        customClass += ' m-button-external';
      } else {
        customClass += ' m-link-tertiary-button-external';
      }
    }

    return customClass.trim();
  };

  const renderButton = (
    link?: string,
    linkText?: string,
    isLink?: boolean,
    openInNewTab?: boolean,
    ctaType?: string,
    customClass?: string
  ) => {
    return (
      link &&
      linkText && (
        <Button
          href={link}
          isLink={isLink}
          target={openInNewTab ? '_blank' : '_self'}
          callback={!isLink ? () => buttonCallback(link, openInNewTab || false) : undefined}
          className={getCtaClasses(ctaType || '', openInNewTab || false, customClass)}
          custom_click_track_value={`${trackingProperties?.location}|${linkText}|${
            openInNewTab ? 'external' : 'internal'
          }`}
          buttonCopy={linkText}
        />
      )
    );
  };

  const ctaPrimary = () => {
    return renderButton(
      primaryCtaLink as string,
      primaryCtaLabel as string,
      primaryCtaLink ? true : false,
      openInNewTabPrimary,
      primaryCtaType,
      'custom_click_track my-2'
    );
  };

  const ctaSecondary = () => {
    return renderButton(
      secondaryCtaLink as string,
      secondaryCtaLabel as string,
      secondaryCtaLink ? true : false,
      openInNewTabSecondary,
      secondaryCtaType,
      'custom_click_track property-details__details-container_footer_link text-center'
    );
  };

  return (
    <StyledPropertyDetails
      data-component-name="m-awp-PropertyDetails"
      data-testid="awp-PropertyDetails"
      className={clsx(
        'property-details container d-flex p-0',
        isCardVertical ? 'flex-column' : 'flex-row',
        getClassForBreakpoint(breakpoint, 'flex-nowrap', 'flex-nowrap', 'flex-column flex-nowrap'),
        styleclass
      )}
      isCardVertical={isCardVertical}
    >
      <div className={clsx('property-details__img-container')}>
        {brandLogoPath && (
          <div className="property-details__img-container--logo d-flex align-items-end">
            <LogoContainer
              brand={brandLogoPath}
              aspectRatio={'4:2'}
              width={90}
              height={45}
              isLink={false}
              ariaRole="img"
              alignment={'Bottom Left'}
              backgroundColor={LogoContainerBackgroundColor.Inverse}
              color={'background-aware'}
            />
          </div>
        )}
        {dynamicMedia && dynamicMedia.assetPath && (
          <Image
            altText={dynamicMedia?.altText}
            renditions={dynamicMedia?.renditions}
            dynamic={dynamicMedia?.dynamic}
            defaultImageURL={dynamicMedia?.assetPath}
            customClass="property-details__img-container--img"
            loading={'eager'}
          />
        )}
      </div>

      <div className={clsx('property-details__details-container d-flex flex-column justify-content-between')}>
        <div className={clsx('property-details__details-container_top')}>
          {header && (
            <div className="d-flex flex-row align-items-baseline mb-1">
              {logoPath && <Icon iconClass={clsx('icon-decorative pr-1', logoPath)}></Icon>}

              {header && (
                <Heading
                  customClass={clsx(`property-details__title mb-0`, headerFontSize)}
                  variation={Types.headingType.subtitle}
                  fontSize={headerFontSize === 't-subtitle-xl' ? Types.size.extraLarge : Types.size.medium}
                  dangerouslySet={header}
                  element={headerTag || Types.tags.div}
                />
              )}
            </div>
          )}

          {subHeader && (
            <Heading
              customClass={clsx(`property-details__subTitle mb-0`, subHeaderFontSize)}
              variation={Types.headingType.body}
              fontSize={Types.size.small}
              dangerouslySet={subHeader}
              element={subHeaderTag || Types.tags.div}
            />
          )}

          {(city || state || country) && (
            <Text
              copyText={getOutletShortAddress(city, state, country)}
              customClass="property-details__address mt-0 t-font-alt-s"
              fontSize={size.small}
              element={tags.span}
            />
          )}

          {descriptionText && (
            <RichText
              customClass={clsx(
                `property-details__details-container_desc mt-3`,
                isCardVertical ? 'mb-3' : getClassForBreakpoint(breakpoint, 'mb-4', 'mb-4', 'mb-3'),
                descriptionFontSize
              )}
              text={descriptionText}
              componentId="property-details-description"
            />
          )}

          {ctaPrimary()}
        </div>
        <div className="property-details__details-container_bottom">
          <div className={clsx('separator')} />
          <div
            className={clsx(
              'property-card__details-container_footer d-flex pl-3 justify-content-end py-3',
              isCardVertical
                ? getClassForBreakpoint(breakpoint, 'pr-3', 'pr-3', 'pr-3')
                : getClassForBreakpoint(breakpoint, 'pr-4', 'pr-4', 'pr-3')
            )}
          >
            <div className="footer-container d-flex">{ctaSecondary()}</div>{' '}
          </div>
        </div>
      </div>
    </StyledPropertyDetails>
  );
};

export const PropertyDetailsConfig = {
  emptyLabel: 'propertydetails',
  isEmpty: true,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/golf/propertydetails`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PropertyDetailsEditable: FC<any> = props => (
  <EditableComponent config={PropertyDetailsConfig} {...props}>
    <PropertyDetails {...props} />
  </EditableComponent>
);
